import { getEditorTagName, nodeAttrsToDOMAttrs } from '@common/util/schema';
import { syncDomAttrs } from '@portal-core/general/util/sync-dom-attrs';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { ProseMirrorNode } from 'prosemirror-model';
import { Selection } from 'prosemirror-state';
import { Decoration, EditorView } from 'prosemirror-view';
import { Subject, takeUntil } from 'rxjs';

export abstract class NodeViewBase<T extends HTMLElement = HTMLElement> {
  /** A map of applied host attributes used to track attr syncing with syncDomAttrs. */
  private appliedHostAttrs = new Map<string, any>();
  /** Registered arrow key navigation cleanup functions. */
  private arrowKeyCleanupFns: (() => void)[] = [];
  public contentDOM: HTMLElement;
  protected decorations: readonly Decoration[];
  public dom: T;
  protected editorView: EditorView;
  protected getPos: GetPosForNodeView;
  protected isDestroyed: boolean = false;
  protected node: ProseMirrorNode;
  /** Used to unsubscribe observables. E.g. nodeViewBridge.down$. */
  private unsubscribe: Subject<void>;

  get syncNodeOnStylesheetChanges(): boolean {
    return false;
  }

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    private nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>,
    editorTagName?: string
  ) {
    this.decorations = decorations;
    this.node = node;
    this.editorView = editorView;
    this.getPos = getPos;

    // Create the dom element based on the node tag name
    const nodeTagName = editorTagName ?? getEditorTagName(node);
    this.dom = document.createElement(nodeTagName) as T;
    this.syncNodeAttrs(node);

    // Listen to editor events
    this.unsubscribe = new Subject<void>();

    this.nodeViewBridge.down$.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(event => {
      this.onEditorEvent(event);

      if (this.syncNodeOnStylesheetChanges) {
        if (event.type === 'stylesheet-loaded' || event.type === 'stylesheet-toggled' || event.type === 'inline-styles-applied') {
          this.syncNode(this.node, this.decorations);
        }
      }
    });
  }

  update(node: ProseMirrorNode, decorations: readonly Decoration[]) {
    if (node.type !== this.node.type) {
      return false;
    } else {
      this.decorations = decorations;
      this.node = node;
      this.syncNodeAttrs(node);
      this.syncNode(node, decorations);
      return true;
    }
  }

  destroy() {
    for (const cleanup of this.arrowKeyCleanupFns) {
      cleanup();
    }

    this.unsubscribe?.next();
    this.unsubscribe?.complete();
    this.isDestroyed = true;
  }

  ignoreMutation(mutation: MutationRecord): boolean {
    // Only pay attention to mutations inside contentDOM; ignore the rest
    return !this.contentDOM || !this.contentDOM.contains(mutation.target);
  }

  /**
   * Emit an event back to the editor.
   * @param type The event type.
   * @param data The event data.
   */
  protected emitEvent(type: string, data: any) {
    this.nodeViewBridge.sendUp({ type, data });
  }

  protected syncNodeAttrs(node: ProseMirrorNode) {
    // Update the node attributes
    const nodeAttrs = node ? this.getNodeAttrs(nodeAttrsToDOMAttrs(node)) : null;
    // Sync the node attrs to the node host element
    syncDomAttrs(this.dom, nodeAttrs, this.appliedHostAttrs);
  }

  protected syncNodeWhenReady(ready: () => boolean) {
    const tick = () => {
      if (ready()) {
        this.syncNode(this.node, this.decorations);
      } else {
        requestAnimationFrame(tick);
      }
    };

    tick();
  }

  /**
   * Given the node's attrs, returns the attributes to apply to the dom element.
   * By default, this just returns the attrs as-is.
   * @param attrs The node's attrs.
   * @returns The attributes to apply to the dom element.
   */
  getNodeAttrs(attrs: Dictionary<string>): Dictionary<string> {
    return attrs;
  }

  /**
   * Returns the target ProseMirror position for arrow key navigation.
   * Override in subclasses to customize where arrow keys navigate.
   * @param pos The position of the current node.
   * @param dir -1 for left/up, 1 for right/down.
   * @param element The element that received the arrow key event.
   */
  protected getArrowKeyTargetPos(pos: number, dir: -1 | 1, element: HTMLElement): number {
    if (!this.contentDOM) {
      // No editable content — navigate before or after the node
      return dir < 0 ? pos : pos + this.node.nodeSize;
    }

    // Check if the element is before or after the content in the DOM to determine navigation direction.
    const isElementBeforeContent = (this.contentDOM.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING) !== 0;

    if (isElementBeforeContent) {
      // Button is on the left - content is to the right
      return dir < 0 ? pos : pos + 1;
    }

    // Button is on the right - content is to the left
    return dir < 0 ? pos + this.node.nodeSize - 1 : pos + this.node.nodeSize;
  }

  /**
   * Adds an arrow key keydown listener to the given element that navigates the ProseMirror selection
   * out of the current node when an arrow key is pressed. The listener is automatically removed on destroy.
   * @param element The element to add the keydown listener to.
   */
  protected addArrowKeyNavigation(element: HTMLElement) {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
        return;
      }

      event.preventDefault();

      const pos = this.getPos();
      if (pos == null) { // == null check to allow for undefined as well
        return;
      }

      const dir = (event.key === 'ArrowLeft' || event.key === 'ArrowUp') ? -1 : 1;
      const targetPos = this.getArrowKeyTargetPos(pos, dir, element);
      const selection = Selection.near(this.editorView.state.doc.resolve(targetPos), dir);
      const tr = this.editorView.state.tr.setSelection(selection).scrollIntoView();
      this.editorView.dispatch(tr);
      this.editorView.focus();
    };

    element.addEventListener('keydown', handler);
    this.arrowKeyCleanupFns.push(() => element.removeEventListener('keydown', handler));
  }

  onEditorEvent(event: EditorEvent) { }
  syncNode(node?: ProseMirrorNode, decorations?: readonly Decoration[]) { }
}
